<template>
  <div class="site-page">
    <!-- 顶部导航 -->
    <nav class="site-nav">
      <a class="site-nav-brand" href="#top">{{ resumeData.name || '个人主页' }}</a>
      <div class="site-nav-links">
        <a href="#skills">技能</a>
        <a href="#experience">经历</a>
        <a href="#projects">项目</a>
        <a href="#education">教育</a>
      </div>
    </nav>

    <!-- 加载中（避免白屏） -->
    <div v-if="loading" class="site-state site-loading">
      <p>正在加载…</p>
    </div>

    <!-- 错误 -->
    <div v-if="error" class="site-state">
      <p>{{ error }}</p>
      <button class="site-btn" @click="fetchResumeData">重试</button>
    </div>

    <template v-if="!loading && !error">
      <!-- 首屏 -->
      <section id="top" class="site-hero">
        <h1 class="site-hero-item" style="animation-delay: 0.05s">{{ resumeData.name }}</h1>
        <p class="site-hero-item site-hero-subtitle" style="animation-delay: 0.15s">{{ resumeData.title }}</p>
        <p class="site-hero-item site-hero-profile" style="animation-delay: 0.25s">{{ resumeData.profile }}</p>

        <div v-if="intentionText" class="site-hero-item site-hero-tags" style="animation-delay: 0.3s">
          <span v-for="(tag, i) in intentionText" :key="i" class="site-tag">{{ tag }}</span>
        </div>

        <div v-if="resumeData.contact" class="site-hero-item site-hero-contact" style="animation-delay: 0.35s">
          <a v-if="resumeData.contact.email" :href="'mailto:' + resumeData.contact.email" class="site-link">{{ resumeData.contact.email }}</a>
          <span v-if="resumeData.contact.phone">{{ resumeData.contact.phone }}</span>
          <a v-if="resumeData.contact.github" :href="'https://' + resumeData.contact.github" target="_blank" rel="noopener" class="site-link">GitHub ↗</a>
        </div>
      </section>

      <!-- 专业技能 -->
      <section id="skills" class="site-section site-section-alt site-reveal">
        <div class="site-section-inner">
          <h2 class="site-section-title">专业技能</h2>
          <div class="site-skills">
            <div class="site-skill" v-for="(skill, index) in resumeData.skills" :key="index">
              <div class="site-skill-head">
                <span class="site-skill-name">{{ skill.name }}</span>
                <span class="site-skill-level">{{ skill.level }}</span>
              </div>
              <div class="site-skill-bar">
                <div class="site-skill-fill" :style="{ width: skillWidth(skill) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 工作经历 -->
      <section id="experience" class="site-section site-reveal">
        <div class="site-section-inner">
          <h2 class="site-section-title">工作经历</h2>
          <div class="site-timeline">
            <article class="site-entry" v-for="(item, index) in resumeData.experience" :key="index">
              <header class="site-entry-head">
                <h3 class="site-entry-title">{{ item.company }}</h3>
                <span class="site-entry-period">{{ item.period }}</span>
              </header>
              <p class="site-entry-meta">{{ item.role }}<template v-if="item.industry"> · {{ item.industry }}</template></p>
              <p class="site-entry-desc">{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <!-- 项目经历（时间轴） -->
      <section id="projects" class="site-section site-section-alt site-reveal" v-if="resumeData.projects.length">
        <div class="site-section-inner">
          <h2 class="site-section-title">项目经历</h2>
          <ol class="site-tl">
            <li class="site-tl-item" v-for="(item, index) in resumeData.projects" :key="index">
              <span class="site-tl-dot" aria-hidden="true"></span>
              <div class="site-tl-content">
                <div class="site-tl-head">
                  <h3 class="site-tl-title">{{ item.name }}</h3>
                  <span class="site-tl-period">{{ item.period }}</span>
                </div>
                <p class="site-tl-role">{{ item.role }}</p>
                <p class="site-tl-desc">{{ item.overview }}</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <!-- 教育背景 -->
      <section id="education" class="site-section site-reveal">
        <div class="site-section-inner">
          <h2 class="site-section-title">教育背景</h2>
          <div class="site-cards">
            <article class="site-card" v-for="(item, index) in resumeData.education" :key="index">
              <h3 class="site-card-title">{{ item.school }}</h3>
              <p class="site-card-meta">{{ item.degree }}</p>
              <p class="site-card-period">{{ item.period }}</p>
            </article>
          </div>
        </div>
      </section>
    </template>

    <footer class="site-footer">
      <p>{{ resumeData.name }} · zhaozhenlei.com</p>
      <p class="site-footer-beian">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">豫ICP备2020027687号-1</a>
      </p>
    </footer>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import resumeApi from '@/api/request'

export default {
  setup() {
    const resumeData = ref({
      name: '',
      title: '',
      profile: '',
      intention: { city: '', type: '' },
      contact: { phone: '', email: '', github: '' },
      education: [],
      experience: [],
      projects: [],
      skills: []
    })
    const loading = ref(true)
    const error = ref(null)
    // 技能条初始宽度为 0，进入视口后填充到目标值（填充动画）
    const skillRevealed = ref(false)

    const intentionText = computed(() => {
      const it = resumeData.value.intention || {}
      return [it.city, it.type].filter(Boolean)
    })

    const skillWidth = (skill) => {
      if (!skillRevealed.value) return 0
      return (skill.level === '熟练' || skill.level === '熟悉') ? 85 : 60
    }

    const fetchResumeData = async () => {
      try {
        loading.value = true
        error.value = null
        // 竞速超时：避免网络异常时一直停在"加载中"
        const response = await Promise.race([
          resumeApi.getResumeData(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('请求超时，请检查网络后重试')), 15000))
        ])
        const resp = response.data || {}
        const d = resp.data || resp || {}
        resumeData.value = {
          name: d.name || '',
          title: d.title || '',
          profile: d.profile || '',
          intention: d.intention || { city: '', type: '' },
          contact: d.contact || { phone: '', email: '', github: '' },
          education: d.education || [],
          experience: d.experience || [],
          projects: d.projects || [],
          skills: d.skills || []
        }
      } catch (err) {
        error.value = (err && err.message) || '获取数据失败'
      } finally {
        loading.value = false
      }
    }

    // 滚动入场：给 .site-reveal 加 .in-view；技能区块进入视口后填充进度条
    const setupReveal = () => {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view')
            io.unobserve(entry.target)
          }
        })
      }, { threshold: 0.12 })
      document.querySelectorAll('.site-reveal').forEach((el) => io.observe(el))

      const skillIo = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => { skillRevealed.value = true })
            skillIo.unobserve(entry.target)
          }
        })
      }, { threshold: 0.2 })
      const skillSection = document.querySelector('#skills')
      if (skillSection) skillIo.observe(skillSection)
    }

    onMounted(async () => {
      await fetchResumeData()
      await nextTick()
      setupReveal()
    })

    return { resumeData, loading, error, fetchResumeData, skillWidth, intentionText }
  }
}
</script>

<style scoped>
/* ===== 设计令牌 ===== */
.site-page {
  --bg: #FFFFFF;
  --bg-alt: #F5F5F7;
  --text: #1D1D1F;
  --text-2: #6E6E73;
  --text-3: #86868B;
  --accent: #0066CC;
  --accent-hover: #0077ED;
  --radius: 18px;
  --section-gap: 96px;

  position: relative;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: 'SF Pro Display', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  scroll-behavior: smooth;
}

/* 顶部导航 */
.site-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 48px;
  height: 52px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.site-nav-brand {
  font-weight: 700;
  font-size: 18px;
  color: var(--text);
  text-decoration: none;
  letter-spacing: -0.01em;
}
.site-nav-links a {
  position: relative;
  color: var(--text-2);
  text-decoration: none;
  margin-left: 26px;
  font-size: 14px;
  transition: color 0.2s ease;
}
.site-nav-links a::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  height: 2px;
  width: 0;
  background: var(--accent);
  transition: width 0.25s ease;
}
.site-nav-links a:hover { color: var(--text); }
.site-nav-links a:hover::after { width: 100%; }

/* 首屏 */
@keyframes siteRise {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
.site-hero {
  text-align: center;
  padding: 132px 24px 96px;
  background: var(--bg);
}
.site-hero-item {
  opacity: 0;
  animation: siteRise 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.site-hero h1 {
  font-size: 60px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 0 0 14px;
}
.site-hero-subtitle {
  font-size: 26px;
  font-weight: 600;
  color: var(--text-2);
  margin: 0 0 14px;
}
.site-hero-profile {
  font-size: 18px;
  color: var(--text-2);
  max-width: 660px;
  margin: 0 auto 22px;
  line-height: 1.55;
}
.site-hero-tags {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 22px;
}
.site-tag {
  font-size: 13px;
  color: var(--text-2);
  background: var(--bg-alt);
  border-radius: 980px;
  padding: 6px 14px;
}
.site-hero-contact {
  display: flex;
  gap: 26px;
  justify-content: center;
  flex-wrap: wrap;
  color: var(--text-2);
  font-size: 16px;
}
.site-link {
  color: var(--accent);
  text-decoration: none;
}
.site-link:hover { text-decoration: underline; }

/* 区块 */
.site-section {
  padding: var(--section-gap) 24px;
  background: var(--bg);
}
.site-section-alt { background: var(--bg-alt); }
.site-section-inner { max-width: 980px; margin: 0 auto; }
.site-section-title {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 40px;
}

/* 滚动入场 */
.site-reveal {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}
.site-reveal.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* 技能 */
.site-skills { max-width: 720px; }
.site-skill { margin-bottom: 26px; }
.site-skill-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}
.site-skill-name { font-size: 17px; font-weight: 600; }
.site-skill-level { font-size: 14px; color: var(--text-3); }
.site-skill-bar {
  height: 7px;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 980px;
  overflow: hidden;
}
.site-skill-fill {
  height: 100%;
  width: 0;
  background: var(--text);
  border-radius: 980px;
  transition: width 1s cubic-bezier(0.22, 1, 0.36, 1);
}

/* 时间线（工作经历） */
.site-timeline { display: flex; flex-direction: column; gap: 18px; }
.site-entry {
  background: var(--bg-alt);
  border-radius: var(--radius);
  padding: 30px 32px;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;
}
.site-entry:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.08);
}
.site-entry-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  flex-wrap: wrap;
}
.site-entry-title { font-size: 21px; font-weight: 600; margin: 0; }
.site-entry-period { font-size: 14px; color: var(--text-3); white-space: nowrap; }
.site-entry-meta { font-size: 15px; color: var(--text-2); margin: 8px 0 12px; }
.site-entry-desc { font-size: 16px; color: var(--text-2); line-height: 1.6; margin: 0; }

/* 时间轴（项目经历） */
.site-tl {
  list-style: none;
  margin: 0;
  padding: 0 0 0 30px;
  position: relative;
}
.site-tl::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: rgba(0, 0, 0, 0.12);
}
.site-tl-item {
  position: relative;
  padding-bottom: 36px;
}
.site-tl-item:last-child { padding-bottom: 0; }
.site-tl-dot {
  position: absolute;
  left: -30px;
  top: 7px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: var(--text);
  box-shadow: 0 0 0 4px var(--bg-alt);
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.site-tl-item:hover .site-tl-dot { transform: scale(1.3); }
.site-tl-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  flex-wrap: wrap;
}
.site-tl-title { font-size: 19px; font-weight: 600; margin: 0; letter-spacing: -0.005em; }
.site-tl-period { font-size: 13px; color: var(--text-3); white-space: nowrap; }
.site-tl-role { font-size: 14px; color: var(--text-2); margin: 6px 0 8px; }
.site-tl-desc { font-size: 15px; color: var(--text-2); line-height: 1.6; margin: 0; }

/* 卡片（教育经历） */
.site-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 22px;
}
.site-card {
  background: var(--bg-alt);
  border-radius: var(--radius);
  padding: 26px 28px;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;
}
.site-section:not(.site-section-alt) .site-card { background: var(--bg-alt); }
.site-section-alt .site-card { background: var(--bg); }
.site-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.08);
}
.site-card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.site-card-title { font-size: 19px; font-weight: 600; margin: 0; }
.site-card-period { font-size: 13px; color: var(--text-3); white-space: nowrap; }
.site-card-meta { font-size: 14px; color: var(--text-2); margin: 8px 0 10px; }
.site-card-desc { font-size: 15px; color: var(--text-2); line-height: 1.6; margin: 0; }

/* 状态 / 按钮 */
.site-state {
  text-align: center;
  padding: 80px 24px;
  font-size: 17px;
  color: var(--text-2);
}
.site-loading {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-3);
}
.site-btn {
  display: inline-block;
  margin-top: 16px;
  padding: 11px 26px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 980px;
  font-size: 16px;
  cursor: pointer;
}
.site-btn:hover { background: var(--accent-hover); }

/* 页脚 */
.site-footer {
  text-align: center;
  padding: 44px 24px;
  background: var(--bg);
  color: var(--text-3);
  font-size: 13px;
}
.site-footer p { margin: 0; }
.site-footer-beian { margin-top: 8px !important; }
.site-footer-beian a {
  color: var(--text-3);
  text-decoration: none;
  transition: color 0.2s ease;
}
.site-footer-beian a:hover { color: var(--accent); }

/* 响应式 */
@media (max-width: 1068px) {
  .site-hero h1 { font-size: 50px; }
}
@media (max-width: 734px) {
  .site-nav { padding: 0 20px; }
  .site-nav-links a { margin-left: 18px; font-size: 13px; }
  .site-hero { padding: 100px 20px 72px; }
  .site-hero h1 { font-size: 38px; }
  .site-hero-subtitle { font-size: 21px; }
  .site-section { padding: 64px 20px; }
  .site-section-title { font-size: 30px; }
  .site-entry { padding: 24px 22px; }
  .site-tl { padding-left: 24px; }
  .site-tl-dot { left: -24px; }
  .site-tl-item { padding-bottom: 30px; }
}
</style>
